import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import {
  clockWipe,
  type ClockWipeProps,
} from "@remotion/transitions/clock-wipe";
import type { FC } from "react";
import { useVideoConfig } from "remotion";

export type AutoSizedClockWipeProps = Omit<ClockWipeProps, "width" | "height">;

// Remotion's presentation component; width/height are supplied per render.
const ClockWipe = clockWipe({ width: 0, height: 0 }).component;

// Remotion's clockWipe() needs the composition size up front, but builders
// only know the fps. This reads it from the composition instead, so the wipe
// fits every aspect ratio.
export function getClockWipePassedProps(
  {
    presentationDirection,
    presentationProgress,
    passedProps,
  }: Pick<
    TransitionPresentationComponentProps<AutoSizedClockWipeProps>,
    "presentationDirection" | "presentationProgress" | "passedProps"
  >,
  width: number,
  height: number,
): ClockWipeProps {
  const settled =
    presentationDirection === "entering" && presentationProgress >= 1;

  return {
    ...passedProps,
    width,
    height,
    // Remotion keeps the presentation mounted for the whole clip, so the
    // entering scene would stay clipped to a full circle (with soft corner
    // pixels) after the wipe. innerEnterStyle is spread after the clip.
    innerEnterStyle: settled
      ? { ...passedProps.innerEnterStyle, clipPath: undefined }
      : passedProps.innerEnterStyle,
  };
}

const AutoSizedClockWipePresentation: FC<
  TransitionPresentationComponentProps<AutoSizedClockWipeProps>
> = (props) => {
  const { width, height } = useVideoConfig();

  return (
    <ClockWipe
      {...props}
      passedProps={getClockWipePassedProps(props, width, height)}
    />
  );
};

export function autoSizedClockWipe(
  props: AutoSizedClockWipeProps = {},
): TransitionPresentation<AutoSizedClockWipeProps> {
  return { component: AutoSizedClockWipePresentation, props };
}
